import { useCallback } from 'react'
import { useThreatProfilesApiClient } from './client'
import { useMutation, useQuery, generateKeys } from '@maui/react-query'
import {
  buildOptimisticInterestMutation,
  buildOptimisticRecommendationFeedbackMutation,
  buildProfileFollowingData,
} from '@/api/ThreatProfiles/utils'
import { useThreatProfilesTokenClaims } from './useThreatProfilesTokenClaims'

const keyScope = '@nautilusjs/mati-apis/threatProfiles'

const keys = generateKeys([
  ['profile', id => id],
  ['profiles'],
], keyScope)

export const useListThreatProfilesQuery = (options = {}) => {
  const api = useThreatProfilesApiClient('profiles')
  const claims = useThreatProfilesTokenClaims()

  // Transform to filter the list to only profiles accessible with the current org JWT. This is
  // necessary as currently the list API returns all profiles cross org but then will 403 when you
  // attempt to fetch one cross org.
  const transform = data => {
    return {
      ...data,
      profiles: (data.profiles || []).filter(item => item.organizationId === claims.org)
    }
  }

  return useQuery({
    ...options,
    queryFn: () => api.list().then(transform),
    queryKey: keys.profiles(),
  })
}

export const useGetThreatProfileQuery = (id, options = {}) => {
  const api = useThreatProfilesApiClient('profiles')
  const transform = profile => ({
    profile,
    following: buildProfileFollowingData(profile)
  })
  const adapters = {
    profile: data => data.profile,
    following: data => data.following,
    none: data => data,
  }
  adapters.default = adapters.profile

  return useQuery({
    ...options,
    queryFn: () => api.get({ id }).then(transform),
    queryKey: keys.profile(id),
    enabled: (options.enabled ?? true) && typeof id === 'string',
    select: options.select || adapters[options.adapter] || adapters.default
  })
}

export const useCreateThreatProfileMutation = (options = {}) => {
  const api = useThreatProfilesApiClient('profiles')

  return useMutation({
    ...options,
    mutationFn: args => api.create(args),
  })
}

export const useUpdateThreatProfileMutation = (profileId, options = {}) => {
  if (typeof profileId === 'object') {
    options = profileId
    profileId = undefined
  }

  const api = useThreatProfilesApiClient('profiles')

  return useMutation({
    ...options,
    mutationFn: args => api.update({ id: profileId, ...args }),
  })
}

export const useRemoveThreatProfileMutation = (options = {}) => {
  const api = useThreatProfilesApiClient('profiles')

  return useMutation({
    ...options,
    mutationFn: ({ id }) => api.remove({ id }),
  })
}

export const useThreatProfileRecommendationFeedbackMutation = (options = {}) => {
  const api = useThreatProfilesApiClient('profiles')

  return useMutation({
    ...options,
    mutationFn: ({ id, interest, feedback }) => api.setRecommendationFeedback({ id, interest, feedback }),
    optimisticUpdateKey: ({ id }) => {
      return keys.profile(id)
    },
    optimisticUpdateFn: ({ profile }, update) => {
      return buildOptimisticRecommendationFeedbackMutation(profile, update)
    }
  })
}

export const useThreatProfileInterestsMutation = (profileId, options = {}) => {
  if (typeof profileId === 'object') {
    options = profileId
    profileId = undefined
  }

  const api = useThreatProfilesApiClient('profiles')

  return useMutation({
    ...options,
    mutationFn: ({ id, interest, type }) => {
      const op = {
        add: 'addInterest',
        remove: 'removeInterest',
      }[type]

      if (!op) {
        throw new Error(
          `useProfileInterestMutation mutate type must be one of: [add, remove], got: '${type}'`
        )
      }

      return api[op]({ id: id || profileId, interest })
    },
    optimisticUpdateKey: ({ id }) => {
      return keys.profile(id || profileId)
    },
    optimisticUpdateFn: ({ profile }, update) => {
      return buildOptimisticInterestMutation(profile, update)
    },
  })
}

export const useThreatProfileFollowing = profileId => {
  const { mutate } = useThreatProfileInterestsMutation(profileId)
  const { data: following = {}, isLoading } = useGetThreatProfileQuery(profileId, {
    adapter: 'following'
  })

  const testFollowing = useCallback(id => {
    return !!id && !!following.all?.has(id)
  }, [following])

  const updateFollowing = useCallback((id, follow) => {
    mutate({ type: follow ? 'add' : 'remove', interest: id })
  }, [mutate])

  const toggleFollowing = useCallback(id => {
    updateFollowing(id, !testFollowing(id))
  }, [updateFollowing, testFollowing])

  return {
    following,
    isLoading,
    testFollowing,
    toggleFollowing,
    updateFollowing,
  }
}
